// Chakra imports
import {
  Box,
  Text,
  useColorModeValue,
  Icon,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  Button,
  Badge,
  Divider,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
// Custom components
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MdArrowBack, MdTrendingUp, MdLink } from "react-icons/md";
import { formatDate, formatNumber } from "utils/format";
import systemService from "services/systemService";

export default function ViewReport() {
  const { date } = useParams();
  const navigate = useNavigate();
  const today = new Date();
  const fallbackDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const reportDate = date || fallbackDate;

  // Chakra Color Mode - All hooks must be called at the top level
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const badgeBg = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await systemService.getDailyReportByDate(reportDate);
        if (result.success && result.data) {
          setReport(result.data);
        } else {
          setError(result.error || 'Failed to load report');
        }
      } catch (err) {
        console.error('Error fetching report:', err);
        setError('Failed to load report. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportDate]);

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Alert status="error" borderRadius="15px" mb="20px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
        <Button
          leftIcon={<Icon as={MdArrowBack} />}
          onClick={() => navigate('/admin/monitoring')}
          colorScheme="blue">
          Back to Monitoring
        </Button>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Alert status="info" borderRadius="15px" mb="20px">
          <AlertIcon />
          <Text color={textColor}>Report not found</Text>
        </Alert>
        <Button
          leftIcon={<Icon as={MdArrowBack} />}
          onClick={() => navigate('/admin/monitoring')}
          colorScheme="blue">
          Back to Monitoring
        </Button>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        {/* Header */}
        <Flex align='center' justify='space-between' flexWrap='wrap' gap='15px'>
          <Flex align='center' gap='15px'>
            <Button
              leftIcon={<Icon as={MdArrowBack} />}
              onClick={() => navigate('/admin/monitoring')}
              variant='ghost'
              size='sm'>
              Back
            </Button>
            <Text color={textColor} fontSize='2xl' fontWeight='700'>
              Daily Report - {report.date ? formatDate(report.date) : reportDate}
            </Text>
          </Flex>
          <Flex gap='10px'>
            <Button colorScheme='blue' variant='solid' size='sm'>
              Generate Class List
            </Button>
            <Button colorScheme='green' variant='solid' size='sm'>
              Generate Tuition Report
            </Button>
          </Flex>
        </Flex>

        {/* Summary Statistics */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap='20px'>
          <Card p='20px' bg={cardBg}>
            <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
              Total Requests
            </Text>
            <Text color={textColor} fontSize='2xl' fontWeight='700'>
              {formatNumber(report.total_requests || 0)}
            </Text>
          </Card>
          <Card p='20px' bg={cardBg}>
            <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
              Successful Requests
            </Text>
            <Text color='green.500' fontSize='2xl' fontWeight='700'>
              {formatNumber(report.successful_requests || 0)}
            </Text>
          </Card>
          <Card p='20px' bg={cardBg}>
            <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
              Failed Requests
            </Text>
            <Text color='red.500' fontSize='2xl' fontWeight='700'>
              {formatNumber(report.failed_requests || 0)}
            </Text>
          </Card>
          <Card p='20px' bg={cardBg}>
            <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
              Unique Users
            </Text>
            <Text color={textColor} fontSize='2xl' fontWeight='700'>
              {formatNumber(report.unique_users || 0)}
            </Text>
          </Card>
        </SimpleGrid>

        {/* Additional Statistics */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdTrendingUp} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Performance Metrics
            </Text>
          </Flex>
          <Divider borderColor={borderColor} mb='20px' />
          <SimpleGrid columns={{ base: 1, md: 2 }} gap='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                Average Response Time
              </Text>
              <Text color={textColor} fontSize='lg' fontWeight='600'>
                {report.average_response_time_ms ? Math.round(report.average_response_time_ms) : 0} ms
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                Report Status
              </Text>
              <Badge
                colorScheme={report.sent_at ? 'green' : 'gray'}
                bg={badgeBg}
                color={textColor}
                fontSize='sm'
                fontWeight='600'
                px='12px'
                py='6px'
                borderRadius='8px'
              >
                {report.sent_at ? 'SENT' : 'PENDING'}
              </Badge>
            </Box>
            {report.sent_at && (
              <Box>
                <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                  Sent At
                </Text>
                <Text color={textColor} fontSize='md'>
                  {formatDate(report.sent_at)}
                </Text>
              </Box>
            )}
            {report.report_url && (
              <Box>
                <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                  Report URL
                </Text>
                <Button
                  as="a"
                  href={report.report_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  leftIcon={<Icon as={MdLink} />}
                  colorScheme="blue"
                  variant="link">
                  View PDF Report
                </Button>
              </Box>
            )}
          </SimpleGrid>
        </Card>

        {/* Requests by Method */}
        {report.requests_by_method && Object.keys(report.requests_by_method).length > 0 && (
          <Card p='30px' bg={cardBg}>
            <Text color={textColor} fontSize='lg' fontWeight='600' mb='20px'>
              Requests by Method
            </Text>
            <Divider borderColor={borderColor} mb='20px' />
            <SimpleGrid columns={{ base: 2, md: 4 }} gap='15px'>
              {Object.entries(report.requests_by_method).map(([method, count]) => (
                <Box key={method} p='15px' bg={badgeBg} borderRadius='8px'>
                  <Text color={textColorSecondary} fontSize='xs' mb='5px'>
                    {method}
                  </Text>
                  <Text color={textColor} fontSize='lg' fontWeight='700'>
                    {formatNumber(count)}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Card>
        )}

        {/* Top Endpoints */}
        {report.top_endpoints && report.top_endpoints.length > 0 && (
          <Card p='30px' bg={cardBg}>
            <Text color={textColor} fontSize='lg' fontWeight='600' mb='20px'>
              Top Endpoints
            </Text>
            <Divider borderColor={borderColor} mb='20px' />
            <Box overflowX='auto'>
              <Table variant='simple' color='secondaryGray.700'>
                <Thead>
                  <Tr>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Endpoint
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600' isNumeric>
                      Request Count
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {report.top_endpoints.slice(0, 10).map((endpoint, index) => (
                    <Tr key={index}>
                      <Td>
                        <Text color={textColor} fontSize='sm' fontFamily='mono'>
                          {endpoint.endpoint || endpoint}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text color={textColor} fontSize='sm' fontWeight='600'>
                          {formatNumber(typeof endpoint === 'object' ? endpoint.request_count : endpoint)}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Card>
        )}

        {/* Top Users */}
        {report.top_users && report.top_users.length > 0 && (
          <Card p='30px' bg={cardBg}>
            <Text color={textColor} fontSize='lg' fontWeight='600' mb='20px'>
              Top Users
            </Text>
            <Divider borderColor={borderColor} mb='20px' />
            <Box overflowX='auto'>
              <Table variant='simple' color='secondaryGray.700'>
                <Thead>
                  <Tr>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      User
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600' isNumeric>
                      Request Count
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {report.top_users.slice(0, 10).map((user, index) => (
                    <Tr key={index}>
                      <Td>
                        <Box>
                          <Text color={textColor} fontSize='sm' fontWeight='600'>
                            {user.user_name || user.user_email || user.user_id || 'Unknown'}
                          </Text>
                          {user.user_email && (
                            <Text color={textColorSecondary} fontSize='xs'>
                              {user.user_email}
                            </Text>
                          )}
                          {user.user_role && (
                            <Badge
                              colorScheme='blue'
                              bg={badgeBg}
                              color={textColor}
                              fontSize='xs'
                              fontWeight='500'
                              px='6px'
                              py='2px'
                              borderRadius='4px'
                              ml='5px'
                            >
                              {user.user_role}
                            </Badge>
                          )}
                        </Box>
                      </Td>
                      <Td isNumeric>
                        <Text color={textColor} fontSize='sm' fontWeight='600'>
                          {formatNumber(user.request_count || user)}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Card>
        )}

        {/* Hourly Statistics */}
        {report.hourly_statistics && report.hourly_statistics.length > 0 && (
          <Card p='30px' bg={cardBg}>
            <Text color={textColor} fontSize='lg' fontWeight='600' mb='20px'>
              Hourly Statistics
            </Text>
            <Divider borderColor={borderColor} mb='20px' />
            <Accordion allowToggle>
              {report.hourly_statistics
                .filter(hour => hour.total_requests > 0)
                .map((hour, index) => (
                  <AccordionItem key={index} borderColor={borderColor}>
                    <AccordionButton>
                      <Box flex='1' textAlign='left'>
                        <Flex align='center' justify='space-between'>
                          <Text color={textColor} fontWeight='600'>
                            Hour {hour.hour}:00 - {hour.hour + 1}:00
                          </Text>
                          <Flex gap='20px' align='center'>
                            <Text color={textColorSecondary} fontSize='sm'>
                              Total: {formatNumber(hour.total_requests)}
                            </Text>
                            <Badge colorScheme='green' fontSize='xs'>
                              Success: {formatNumber(hour.successful_requests)}
                            </Badge>
                            <Badge colorScheme='red' fontSize='xs'>
                              Failed: {formatNumber(hour.failed_requests)}
                            </Badge>
                          </Flex>
                        </Flex>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <SimpleGrid columns={{ base: 1, md: 2 }} gap='20px' mt='15px'>
                        {hour.average_response_time_ms > 0 && (
                          <Box>
                            <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                              Avg Response Time
                            </Text>
                            <Text color={textColor} fontSize='md' fontWeight='600'>
                              {Math.round(hour.average_response_time_ms)} ms
                            </Text>
                          </Box>
                        )}
                        {hour.unique_users > 0 && (
                          <Box>
                            <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                              Unique Users
                            </Text>
                            <Text color={textColor} fontSize='md' fontWeight='600'>
                              {formatNumber(hour.unique_users)}
                            </Text>
                          </Box>
                        )}
                        {hour.requests_by_method && Object.keys(hour.requests_by_method).length > 0 && (
                          <Box>
                            <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='10px'>
                              Requests by Method
                            </Text>
                            <Flex direction='column' gap='5px'>
                              {Object.entries(hour.requests_by_method).map(([method, count]) => (
                                <Flex key={method} justify='space-between'>
                                  <Text color={textColor} fontSize='sm'>{method}</Text>
                                  <Text color={textColor} fontSize='sm' fontWeight='600'>{formatNumber(count)}</Text>
                                </Flex>
                              ))}
                            </Flex>
                          </Box>
                        )}
                        {hour.top_endpoints && hour.top_endpoints.length > 0 && (
                          <Box>
                            <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='10px'>
                              Top Endpoints
                            </Text>
                            <Flex direction='column' gap='5px'>
                              {hour.top_endpoints.slice(0, 5).map((endpoint, idx) => (
                                <Flex key={idx} justify='space-between'>
                                  <Text color={textColor} fontSize='sm' fontFamily='mono' isTruncated maxW='200px'>
                                    {typeof endpoint === 'object' ? endpoint.endpoint : endpoint}
                                  </Text>
                                  <Text color={textColor} fontSize='sm' fontWeight='600'>
                                    {formatNumber(typeof endpoint === 'object' ? endpoint.request_count : 0)}
                                  </Text>
                                </Flex>
                              ))}
                            </Flex>
                          </Box>
                        )}
                      </SimpleGrid>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
            </Accordion>
          </Card>
        )}

        {/* Recipients */}
        {report.recipients && report.recipients.length > 0 && (
          <Card p='30px' bg={cardBg}>
            <Text color={textColor} fontSize='lg' fontWeight='600' mb='20px'>
              Report Recipients
            </Text>
            <Divider borderColor={borderColor} mb='20px' />
            <Flex direction='column' gap='10px'>
              {report.recipients.map((recipient, index) => (
                <Text key={index} color={textColor} fontSize='sm'>
                  {recipient}
                </Text>
              ))}
            </Flex>
          </Card>
        )}
      </Flex>
    </Box>
  );
}

